// @givmo/org-console — the organization's brand and collateral library on the wire (Email Campaign
// plan EMC-WEB11; D-EMC-20 (c), (d)): `/org/{org_ref}/profile/branding` and
// `/org/{org_ref}/profile/collateral`, the backend's `org_brand` router (be#3889, EMC-BE14's Delete).
//
// ══ HAND-WRITTEN, LIKE EVERY PANEL OPERATION HERE (§3) ══════════════════════════════════════════
//
// The committed OpenAPI snapshot predates the router, so each operation is a `givmoClient` call whose
// URL a human wrote. The client is the one the generated operations bind to: bearer token, App Check,
// `X-Request-Id` and the `GivmoError` mapping all apply. An upload is ONE multipart POST with a
// `file` part, the form built here exactly as the generated `formDataBodySerializer` builds it.
//
// ══ DARK IS A 404, CLASSIFIED ON STATUS ALONE (§4) ══════════════════════════════════════════════
//
// The router is triple-gated (`ORG_CONSOLE_ENABLED`, `FUNDRAISERS_MODULE_ENABLED`,
// `EMAIL_CAMPAIGNS_ENABLED`) and answers the `/org` route miss while any is off, so the tabs show a
// calm not-available state on a 404 and nothing else (`isOrgConsoleUnavailable`).
//
// ══ THE DECODE NEVER THROWS ═════════════════════════════════════════════════════════════════════
//
// A colour with no readable value, an item with no address: dropped, never a blank screen. A role or
// a font this build does not know is kept out of what it draws, and the PUT re-sends only what was
// drawn, so the server's validation stays the authority on every value.

import { firstErrorCode, givmoClient, isGivmoError } from '@givmo/api-client';
import { PROFILE_COPY } from './profile-copy';

const REFUSALS = PROFILE_COPY.uploadRefusals;

/** Where a colour is used. Each role is on at most one colour (the server refuses two). */
export type BrandColorRole = 'headings' | 'buttons' | 'links' | 'accents' | 'text' | 'qr';
export const BRAND_COLOR_ROLES: readonly BrandColorRole[] = [
  'headings',
  'buttons',
  'links',
  'accents',
  'text',
  'qr',
];

export type BrandLogoSlot = 'primary' | 'alternate' | 'dark';
export const BRAND_LOGO_SLOTS: readonly BrandLogoSlot[] = ['primary', 'alternate', 'dark'];

export type BrandFontWeight = 'regular' | 'bold';

export interface BrandColor {
  name: string;
  /** `#rrggbb`, lowercase. */
  hex: string;
  roles: BrandColorRole[];
  /**
   * Roles a later server names that this build does not draw: kept off the screen and written back
   * unchanged, so a Save never erases them.
   */
  otherRoles: string[];
}

export interface BrandFontChoice {
  /** A key of `fontChoices`. */
  family: string;
  weight: BrandFontWeight;
  /** The CSS `font-family` the server writes for it. */
  cssStack: string;
}

export interface BrandFonts {
  headings: BrandFontChoice;
  body: BrandFontChoice;
  fallback: string;
}

export interface EmailSafeFont {
  key: string;
  label: string;
  /** `sans-serif` | `serif` | `monospace`: the family a sample falls back to. */
  generic: string;
}

export interface BrandLogo {
  /** Stable: a Replace overwrites the file behind it. */
  url: string;
  contentType: string;
}

export interface OrgBrand {
  /** False until the brand is first saved or a logo first uploaded. */
  saved: boolean;
  colors: BrandColor[];
  fonts: BrandFonts | null;
  tagline: string | null;
  voiceNotes: string | null;
  logos: Record<BrandLogoSlot, BrandLogo | null>;
  fontChoices: EmailSafeFont[];
  updatedAt: string | null;
}

export type CollateralKind = 'image' | 'file';

export interface CollateralItem {
  id: string;
  kind: CollateralKind;
  title: string;
  description: string | null;
  tags: string[];
  /** `YYYY-MM-DD`, the date the graphic or file is for. */
  datedOn: string | null;
  /** Stable: a Replace overwrites the file behind it. */
  url: string;
  contentType: string;
  fileName: string;
  outOfDate: boolean;
  replacedAt: string | null;
  createdAt: string;
}

export interface CollateralList {
  items: CollateralItem[];
  /** How many items match, across every page. */
  total: number;
  /** Every tag in the library, for the tag filter. */
  tags: string[];
}

/** `PUT …/profile/branding`: the colours, fonts and words, replaced whole. */
export interface OrgBrandPut {
  colors: { name: string; hex: string; roles: string[] }[];
  fonts: {
    headings: { family: string; weight: BrandFontWeight };
    body: { family: string; weight: BrandFontWeight };
    fallback: string;
  } | null;
  tagline: string | null;
  voice_notes: string | null;
}

/** `PATCH …/profile/collateral/{id}`: Edit details. A field left out is untouched. */
export interface CollateralItemPatch {
  title?: string;
  description?: string | null;
  tags?: string[];
  dated_on?: string | null;
}

// ── Decode ───────────────────────────────────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

const HEX = /^#[0-9a-f]{6}$/;

function decodeColor(raw: unknown): BrandColor | null {
  if (!isRecord(raw)) return null;
  const name = str(raw.name);
  const hex = str(raw.hex)?.toLowerCase() ?? null;
  if (name === null || hex === null || !HEX.test(hex)) return null;
  const known = (role: string): role is BrandColorRole =>
    (BRAND_COLOR_ROLES as readonly string[]).includes(role);
  const all = strings(raw.roles);
  return { name, hex, roles: all.filter(known), otherRoles: all.filter((role) => !known(role)) };
}

function decodeFontChoice(raw: unknown): BrandFontChoice | null {
  if (!isRecord(raw)) return null;
  const family = str(raw.family);
  if (family === null) return null;
  return {
    family,
    weight: raw.weight === 'bold' ? 'bold' : 'regular',
    cssStack: str(raw.css_stack) ?? 'Arial, sans-serif',
  };
}

function decodeFonts(raw: unknown): BrandFonts | null {
  if (!isRecord(raw)) return null;
  const headings = decodeFontChoice(raw.headings);
  const body = decodeFontChoice(raw.body);
  if (headings === null || body === null) return null;
  return { headings, body, fallback: str(raw.fallback) ?? 'arial' };
}

function decodeLogo(raw: unknown): BrandLogo | null {
  if (!isRecord(raw)) return null;
  const url = str(raw.url);
  return url === null ? null : { url, contentType: str(raw.content_type) ?? 'image/png' };
}

export function decodeOrgBrand(body: unknown): OrgBrand | null {
  if (!isRecord(body)) return null;
  const logos = isRecord(body.logos) ? body.logos : {};
  const fontChoices: EmailSafeFont[] = [];
  if (Array.isArray(body.font_choices)) {
    for (const raw of body.font_choices) {
      if (!isRecord(raw)) continue;
      const key = str(raw.key);
      if (key !== null) {
        fontChoices.push({
          key,
          label: str(raw.label) ?? key,
          generic: str(raw.generic) ?? 'sans-serif',
        });
      }
    }
  }
  return {
    saved: body.saved === true,
    colors: Array.isArray(body.colors)
      ? body.colors.map(decodeColor).filter((c): c is BrandColor => c !== null)
      : [],
    fonts: decodeFonts(body.fonts),
    tagline: str(body.tagline),
    voiceNotes: str(body.voice_notes),
    logos: {
      primary: decodeLogo(logos.primary),
      alternate: decodeLogo(logos.alternate),
      dark: decodeLogo(logos.dark),
    },
    fontChoices,
    updatedAt: str(body.updated_at),
  };
}

export function decodeCollateralItem(raw: unknown): CollateralItem | null {
  if (!isRecord(raw)) return null;
  const id = str(raw.id);
  const url = str(raw.url);
  if (id === null || url === null) return null;
  return {
    id,
    kind: raw.kind === 'file' ? 'file' : 'image',
    title: str(raw.title) ?? 'Untitled',
    description: str(raw.description),
    tags: strings(raw.tags),
    datedOn: str(raw.dated_on),
    url,
    contentType: str(raw.content_type) ?? '',
    fileName: str(raw.file_name) ?? '',
    outOfDate: raw.out_of_date === true,
    replacedAt: str(raw.replaced_at),
    createdAt: str(raw.created_at) ?? '',
  };
}

export function decodeCollateralList(body: unknown): CollateralList {
  if (!isRecord(body)) return { items: [], total: 0, tags: [] };
  const items = Array.isArray(body.items)
    ? body.items.map(decodeCollateralItem).filter((i): i is CollateralItem => i !== null)
    : [];
  return {
    items,
    total: typeof body.total === 'number' ? body.total : items.length,
    tags: strings(body.tags),
  };
}

/**
 * A stable address asked for afresh after a Replace (`?v=`), since the address itself never changes.
 * Only a web address takes one: a `data:` or `blob:` address is the file itself.
 */
export function versionedUrl(url: string, version: string | null): string {
  if (version === null || !/^https?:/i.test(url)) return url;
  return `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(version)}`;
}

// ── Upload checks ───────────────────────────────────────────────────────────────────────────────
//
// The server's own rules (`app/domain/org_brand.py`: `IMAGE_TYPES`, `FILE_TYPES`, `MAX_IMAGE_BYTES`
// = the house `MAX_LOGO_SIZE`, `MAX_FILE_BYTES`; `OrgBrandService._checked_upload` and its sentences),
// checked before a byte goes up: a file over the cap would otherwise travel whole only to be refused,
// and one past Cloud Run's 32 MiB request bound is refused with no reason at all. The server still
// sniffs every upload and stays the authority; this only says its answer sooner.

/** An image's cap (PNG or JPEG): the server's `MAX_IMAGE_BYTES`, 10 MB. */
export const BRAND_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
/** A PDF's cap: the server's `MAX_FILE_BYTES`, 25 MB. */
export const BRAND_PDF_MAX_BYTES = 25 * 1024 * 1024;

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg'];
const PDF_EXTENSIONS = ['.pdf'];

/** What an upload may be: a logo, a new library item, or a Replace of an image or of a PDF. */
export type BrandUploadTarget = 'logo' | 'collateral' | 'collateral-image' | 'collateral-file';

function kindByName(fileName: string): 'image' | 'file' | null {
  const dot = fileName.lastIndexOf('.');
  const extension = dot >= 0 ? fileName.slice(dot).toLowerCase() : '';
  if (IMAGE_EXTENSIONS.includes(extension)) return 'image';
  if (PDF_EXTENSIONS.includes(extension)) return 'file';
  return null;
}

/**
 * Why the server would refuse this file, in its own words, or null when it may go up. The kind is
 * read from the file's name, as the server reads it first (`extension_allowed`).
 */
export function brandUploadRefusal(file: File, target: BrandUploadTarget): string | null {
  if (file.size === 0) return REFUSALS.empty;
  const kind = kindByName(file.name);
  if (target === 'logo') {
    if (kind !== 'image') return REFUSALS.logoType;
  } else if (kind === null) {
    return REFUSALS.collateralType;
  } else if (
    (target === 'collateral-image' && kind !== 'image') ||
    (target === 'collateral-file' && kind !== 'file')
  ) {
    return REFUSALS.replaceKind;
  }
  if (kind === 'file' && file.size > BRAND_PDF_MAX_BYTES) return REFUSALS.pdfTooLarge;
  if (kind === 'image' && file.size > BRAND_IMAGE_MAX_BYTES) return REFUSALS.imageTooLarge;
  return null;
}

// ── Refusals ─────────────────────────────────────────────────────────────────────────────────────

/** The upload and conflict refusals the server words for the person (its `detail` is said as is). */
const SAID_AS_IS = new Set([
  'BRAND_FILE_TYPE',
  'BRAND_FILE_TOO_LARGE',
  'BRAND_FILE_EMPTY',
  'COLLATERAL_REPLACE_KIND',
  'COLLATERAL_LIMIT',
  'COLLATERAL_IN_USE',
]);

/**
 * What a failed write says: the server's own sentence for a refusal it words for the person (a file
 * it will not take, an item an unsent email still shows); for a body it refused (422), the caller's
 * sentence for the field it names, since the server's own 422 text is a developer's
 * (`body.tags: Value error, …`) and is never shown; else the caller's fallback.
 */
export function brandRefusalMessage(
  error: unknown,
  fallback: string,
  fieldSentences: Readonly<Record<string, string>> = {},
): string {
  if (!isGivmoError(error)) return fallback;
  const code = firstErrorCode(error);
  if (code !== null && SAID_AS_IS.has(code) && error.message.length > 0) return error.message;
  if (error.status === 422) {
    // The detail reads `body.<field>…: <reason>` for each refusal (`handle_request_validation_error`).
    const named = /(?:^|\. )body\.([a-z_]+)/.exec(error.message)?.[1];
    if (named !== undefined && fieldSentences[named] !== undefined) return fieldSentences[named];
  }
  return fallback;
}

// ── Transport ────────────────────────────────────────────────────────────────────────────────────

function base(orgRef: string): string {
  return `/org/${encodeURIComponent(orgRef)}/profile`;
}

/** One multipart POST whose one part is `file`, built as the generated serializer builds it. */
function upload(url: string, file: File) {
  return givmoClient.post({
    url,
    body: { file },
    bodySerializer: (body: unknown) => {
      const form = new FormData();
      const file = (body as { file: File }).file;
      // Named explicitly: the server titles a new library item from the file's name.
      form.append('file', file, file.name);
      return form;
    },
    headers: { 'Content-Type': null },
    throwOnError: true,
  });
}

export function orgBrandKey(orgRef: string) {
  return ['org-brand', orgRef] as const;
}

export function orgCollateralKey(orgRef: string, filter?: { q: string; tag: string | null }) {
  return filter === undefined
    ? (['org-collateral', orgRef] as const)
    : (['org-collateral', orgRef, filter.q, filter.tag] as const);
}

export async function fetchOrgBrand(orgRef: string, signal?: AbortSignal): Promise<OrgBrand> {
  const { data } = await givmoClient.get({
    url: `${base(orgRef)}/branding`,
    signal,
    throwOnError: true,
  });
  const brand = decodeOrgBrand(data);
  if (brand === null) throw new Error('The brand could not be read.');
  return brand;
}

export async function replaceOrgBrand(orgRef: string, body: OrgBrandPut): Promise<OrgBrand> {
  const { data } = await givmoClient.put({
    url: `${base(orgRef)}/branding`,
    body,
    throwOnError: true,
  });
  const brand = decodeOrgBrand(data);
  if (brand === null) throw new Error('The brand could not be read.');
  return brand;
}

export async function uploadOrgBrandLogo(
  orgRef: string,
  slot: BrandLogoSlot,
  file: File,
): Promise<OrgBrand> {
  const { data } = await upload(`${base(orgRef)}/branding/logos/${encodeURIComponent(slot)}`, file);
  const brand = decodeOrgBrand(data);
  if (brand === null) throw new Error('The brand could not be read.');
  return brand;
}

/** The page size the library is read in; the server's own default. */
export const COLLATERAL_PAGE_LIMIT = 100;

export async function fetchOrgCollateral(
  orgRef: string,
  read: {
    q: string;
    tag: string | null;
    offset: number;
    signal?: AbortSignal;
    /** Only the items in use (the server's `status=current`); every item when unset. */
    currentOnly?: boolean;
    /** One kind only; both when unset. */
    kind?: CollateralKind;
  },
): Promise<CollateralList> {
  const query: Record<string, string | number> = {
    limit: COLLATERAL_PAGE_LIMIT,
    offset: read.offset,
  };
  if (read.q.trim().length > 0) query.q = read.q.trim();
  if (read.tag !== null) query.tag = read.tag;
  if (read.currentOnly === true) query.status = 'current';
  if (read.kind !== undefined) query.kind = read.kind;
  const { data } = await givmoClient.get({
    url: `${base(orgRef)}/collateral`,
    query,
    signal: read.signal,
    throwOnError: true,
  });
  return decodeCollateralList(data);
}

function itemUrl(orgRef: string, itemId: string): string {
  return `${base(orgRef)}/collateral/${encodeURIComponent(itemId)}`;
}

function decodedItem(data: unknown): CollateralItem {
  const item = decodeCollateralItem(data);
  if (item === null) throw new Error('The item could not be read.');
  return item;
}

/** One library item by id, whatever its state (EMC-BE23: a campaign's share graphics name items by id). */
export async function fetchOrgCollateralItem(
  orgRef: string,
  itemId: string,
  signal?: AbortSignal,
): Promise<CollateralItem> {
  const { data } = await givmoClient.get({
    url: itemUrl(orgRef, itemId),
    signal,
    throwOnError: true,
  });
  return decodedItem(data);
}

export async function addOrgCollateralItem(orgRef: string, file: File): Promise<CollateralItem> {
  const { data } = await upload(`${base(orgRef)}/collateral`, file);
  return decodedItem(data);
}

export async function replaceOrgCollateralFile(
  orgRef: string,
  itemId: string,
  file: File,
): Promise<CollateralItem> {
  const { data } = await upload(`${itemUrl(orgRef, itemId)}/file`, file);
  return decodedItem(data);
}

export async function updateOrgCollateralItem(
  orgRef: string,
  itemId: string,
  body: CollateralItemPatch,
): Promise<CollateralItem> {
  const { data } = await givmoClient.patch({
    url: itemUrl(orgRef, itemId),
    body,
    throwOnError: true,
  });
  return decodedItem(data);
}

export async function markOrgCollateralItem(
  orgRef: string,
  itemId: string,
  outOfDate: boolean,
): Promise<CollateralItem> {
  const { data } = await givmoClient.post({
    url: `${itemUrl(orgRef, itemId)}/${outOfDate ? 'out-of-date' : 'current'}`,
    throwOnError: true,
  });
  return decodedItem(data);
}

export async function deleteOrgCollateralItem(orgRef: string, itemId: string): Promise<void> {
  await givmoClient.delete({ url: itemUrl(orgRef, itemId), throwOnError: true });
}
